import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, SafeAreaView, TouchableOpacity, useWindowDimensions, ScrollView, Text } from 'react-native';
import { useTheme, useNavigation } from '@react-navigation/native';
import Ionicons from "@react-native-vector-icons/ionicons/static";
import CustomLabel from '../../../../components/CustomLabel/CustomLabel';
import CustomDropDown from '../../../../components/CustomDropDown/CustomDropdown';
import CustomButton from '../../../../components/CustomButton';
import { useTranslation } from 'react-i18next';
import { createCommonStyles } from '../../../../styles/commonStyles';
import { useCompany } from '../../../../hooks/useCompany';
import { showMessage } from 'react-native-flash-message';
import CustomInput from '../../../../components/CustomInput';
import { v4 as uuidv4 } from 'uuid';
import { useAuthContext } from "../../../../hooks/useAuthContext";

const createCriteriaPair = (criteriaObj, chosenValues, parameterOptionsMap) => {
    const value = chosenValues.length > 1 ? chosenValues : chosenValues[0];
    const options = parameterOptionsMap[criteriaObj.rowGuid] || [];
    const getLabel = (val) => {
        const opt = options.find(o => String(o.value).toLowerCase() === String(val).toLowerCase());
        return opt ? opt.label : val;
    };
    const parameterLabel = Array.isArray(value) ? value.map(getLabel).join(', ') : getLabel(value);
    return {
        criteriaId: criteriaObj.rowGuid,
        criteria: criteriaObj.displayMember,
        parameter: parameterLabel,
        value: value,
        valueMember: criteriaObj.valueMember,
        rowGuid: uuidv4(),
    };
};

const areValuesEqual = (val1, val2) => {
    if (Array.isArray(val1) && Array.isArray(val2)) {
        if (val1.length !== val2.length) return false;
        const sorted1 = [...val1].sort();
        const sorted2 = [...val2].sort();
        return sorted1.every((v, i) => String(v) === String(sorted2[i]));
    }
    return String(val1) === String(val2);
};

const criteriaPairsToDescription = (pairs, criteriaList, criteriaOptionsMap) => {
    if (!pairs || pairs.length === 0) return '';
    return pairs.map(pair => {
        const criteriaObj = criteriaList.find(c => c.rowGuid === pair.criteriaId);
        const criteriaName = criteriaObj ? criteriaObj.displayMember : pair.criteriaId || 'Unknown';

        // Normalize value: split comma-separated string into array
        const normalizedValue = typeof pair.value === 'string' && pair.value.includes(',')
            ? pair.value.split(',').map(v => v.trim())
            : pair.value;

        const labelValue = getLabelForValue(pair.criteriaId, normalizedValue, criteriaOptionsMap);
        const lowerCriteria = criteriaName.toLowerCase();
        const connector = (lowerCriteria === 'duration' || lowerCriteria === 'accumulated balance') ? 'like' : 'is';
        return `${criteriaName} ${connector} ${labelValue}`;
    }).join('\n');
};

const getLabelForValue = (criteriaId, value, criteriaOptionsMap) => {
    const options = criteriaOptionsMap[criteriaId] || [];
    if (Array.isArray(value)) {
        const labels = value.map(val => {
            const opt = options.find(o => String(o.value).toLowerCase().trim() === String(val).toLowerCase().trim());
            if (!opt) {
                return val;
            }
            return opt.label;
        });
        return labels.join(', ');
    } else {
        const opt = options.find(o => String(o.value).toLowerCase().trim() === String(value).toLowerCase().trim());
        if (!opt) {
            return value;
        }
        return opt.label;
    }
};

const HRAdminWorkflowCriteria = ({ route, onBack, categoryId, company, selectedWorkflowId, }) => {
    const initialDescription = route?.params?.description || '';
    const description = route?.params?.description || '';
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const { width } = useWindowDimensions();
    const styles = makeStyles(colors, width);
    const { t } = useTranslation();
    const navigation = useNavigation();
    const { getWorkflowCategoryCriteria, saveWorkflowCriteria, getCriteriaOptionsById, getWorkflowCriteriaDesc } = useCompany();
    const [criteriaList, setCriteriaList] = useState([]);
    const [criteriaValues, setCriteriaValues] = useState({});
    const [selectedCriteria, setSelectedCriteria] = useState(null);
    const [selectedCategoryCriteriaId, setSelectedCategoryCriteriaId] = useState(null);
    const [parameterOptions, setParameterOptions] = useState([]);
    const [parameterValue, setParameterValue] = useState('');
    const [isSqlNull, setIsSqlNull] = useState(false);
    const maxHeight = width <= 1366 ? 200 : null;
    const [criteriaDesc, setCriteriaDesc] = useState('');
    const [initialCriteriaDesc, setInitialCriteriaDesc] = useState('');
    const [criteriaSql, setCriteriaSql] = useState('');
    const [allCriteriaPairs, setAllCriteriaPairs] = useState([]);
    const [removedCriteriaIds, setRemovedCriteriaIds] = useState([]);
    const [fetchedCriteriaPairs, setFetchedCriteriaPairs] = useState([]);
    const [initialCriteriaPairs, setInitialCriteriaPairs] = useState([]);
    const [criteriaOptionsMap, setCriteriaOptionsMap] = useState({});
    const { userId } = useAuthContext();

    // Effect to fetch criteria description and pairs (without removedCriteriaIds)
    useEffect(() => {
        if (!company || !selectedWorkflowId || criteriaList.length === 0) {
            setInitialCriteriaDesc('');
            setFetchedCriteriaPairs([]);
            return;
        }
        const allOptionsLoaded = criteriaList.every(c => criteriaOptionsMap.hasOwnProperty(c.rowGuid));
        if (!allOptionsLoaded) return;

        const fetchCriteriaDesc = async () => {
            try {
                const pairs = await getWorkflowCriteriaDesc(company, selectedWorkflowId);
                const descString = criteriaPairsToDescription(pairs, criteriaList, criteriaOptionsMap);
                setInitialCriteriaDesc(descString);
                setCriteriaDesc(descString);

                const parsedPairs = pairs.map(pair => {
                    const criteriaObj = criteriaList.find(c => c.rowGuid === pair.criteriaId);
                    if (!criteriaObj) return null;
                    const chosenValues = typeof pair.value === 'string' && pair.value.includes(',')
                        ? pair.value.split(',').map(v => v.trim())
                        : pair.value || '';
                    const newPair = createCriteriaPair(criteriaObj, Array.isArray(chosenValues) ? chosenValues : [chosenValues], criteriaOptionsMap);
                    newPair.rowGuid = pair.rowGuid;
                    newPair.isInitial = true;
                    return newPair;
                }).filter(Boolean);

                setFetchedCriteriaPairs(parsedPairs);
                setInitialCriteriaPairs(prev => (prev.length === 0 ? parsedPairs : prev));
            } catch (error) {
                console.error('Error fetching workflow criteria description:', error);
                setInitialCriteriaDesc('');
                setFetchedCriteriaPairs([]);
            }
        };
        fetchCriteriaDesc();
    }, [company, selectedWorkflowId, criteriaList, criteriaOptionsMap]);

    // Effect to update allCriteriaPairs by filtering out removed pairs
    useEffect(() => {
        const filteredPairs = fetchedCriteriaPairs.filter(pair => !removedCriteriaIds.includes(pair.rowGuid));
        setAllCriteriaPairs(filteredPairs);
    }, [fetchedCriteriaPairs, removedCriteriaIds]);


    // Effect to fetch criteria list by categoryId
    useEffect(() => {
        const fetchCriteria = async () => {
            if (!categoryId) return;
            const criteria = await getWorkflowCategoryCriteria(categoryId);
            setCriteriaList(criteria);
            if (criteria.length > 0) {
                setSelectedCategoryCriteriaId(criteria[0].rowGuid);
                setIsSqlNull(!criteria[0].sSql);
            }
        };
        fetchCriteria();
    }, [categoryId]);

    // Effect to load parameter options when selectedCategoryCriteriaId changes
    useEffect(() => {
        if (!selectedCategoryCriteriaId) return;
        const fetchParameterOptions = async () => {
            try {
                const selectedCriteria = criteriaList.find(c => c.rowGuid === selectedCategoryCriteriaId);
                if (!selectedCriteria) {
                    setParameterOptions([]);
                    return;
                }
                if (!selectedCriteria.sSql) {
                    setParameterOptions([]);
                    setParameterValue('');
                    setIsSqlNull(true);
                    return;
                }
                setIsSqlNull(false);
                const options = await getCriteriaOptionsById(selectedCriteria.sSql, company);
                setParameterOptions(options || []);
                setParameterValue('');
                setCriteriaOptionsMap(prev => ({
                    ...prev,
                    [selectedCategoryCriteriaId]: options || [],
                }));
            } catch (error) {
                console.error('Failed to fetch parameter options:', error);
                setParameterOptions([]);
            }
        };
        fetchParameterOptions();
    }, [selectedCategoryCriteriaId, criteriaList, company]);

    // Effect to update criteriaDesc and criteriaSql when allCriteriaPairs changes
    useEffect(() => {
        if (allCriteriaPairs.length === 0) {
            setCriteriaDesc('');
            setCriteriaSql('');
            return;
        }
        const descArray = allCriteriaPairs
            .map(p => {
                if (!p.parameter) return null;
                const criteriaLower = p.criteria.toLowerCase();
                const connector = (criteriaLower === 'duration' || criteriaLower === 'accumulated balance') ? 'like' : 'is';
                return `${p.criteria} ${connector} ${p.parameter}`;
            })
            .filter(Boolean);
        setCriteriaDesc(descArray.join('\n'));
        const sql = allCriteriaPairs
            .map(p => {
                if (!p.valueMember || p.value === undefined || p.value === null) return null;
                let formattedVal;
                if (Array.isArray(p.value)) {
                    formattedVal = p.value.map(v => `'${v}'`).join(',');
                } else {
                    formattedVal = `'${p.value}'`;
                }
                return p.valueMember.replace(/@value/g, formattedVal);
            })
            .filter(Boolean)
            .join(' AND ');
        setCriteriaSql(sql);
    }, [allCriteriaPairs]);

    useEffect(() => {
        if (!criteriaList || criteriaList.length === 0) return;

        const fetchAllOptions = async () => {
            const newMap = {};
            for (const crit of criteriaList) {
                if (!crit.sSql) {
                    newMap[crit.rowGuid] = [];
                    continue;
                }
                try {
                    const options = await getCriteriaOptionsById(crit.sSql, company);
                    newMap[crit.rowGuid] = options || [];
                } catch (error) {
                    console.error(`Failed to fetch options for criteria ${crit.displayMember}`, error);
                    newMap[crit.rowGuid] = [];
                }
            }
            setCriteriaOptionsMap(newMap);
        };

        fetchAllOptions();
    }, [criteriaList, company]);

    useEffect(() => {
        if (isSqlNull && selectedCategoryCriteriaId) {
            setCriteriaValues(prev => {
                // If already has a value, keep it, else set empty string
                if (prev[selectedCategoryCriteriaId] === undefined) {
                    return { ...prev, [selectedCategoryCriteriaId]: '' };
                }
                return prev;
            });
            setParameterValue('');
        }
    }, [selectedCategoryCriteriaId, isSqlNull]);

    const validateExpr = (textInput) => {
        if (!textInput) return false;
        const lowerText = textInput.toLowerCase().trim();
        if (lowerText.includes('between')) {
            // Expected format: "between X and Y"
            const parts = lowerText.split(' ');
            if (parts.length === 4 && parts[0] === 'between' && parts[2] === 'and') {
                const num1 = parseFloat(parts[1]);
                const num2 = parseFloat(parts[3]);
                if (isNaN(num1) || isNaN(num2)) return false;
                if (num1 < num2) {
                    const pattern = new RegExp(`^between ${num1} and ${num2}$`);
                    return pattern.test(lowerText);
                }
                return false;
            }
            return false;
        } else {
            const pattern = /^((<=|>=|<|>|=)?-?\d+(\.\d{1,2})?)$/;
            return pattern.test(textInput);
        }
    };

    const handleAddParameter = async () => {
        if (!selectedCategoryCriteriaId) {
            showMessage({ message: t('common:pleaseSelectCriteria'), type: 'warning' });
            return;
        }
        const selectedCriteriaObj = criteriaList.find(c => c.rowGuid === selectedCategoryCriteriaId);
        if (!selectedCriteriaObj) return;

        const rawInput = isSqlNull
            ? (criteriaValues[selectedCategoryCriteriaId] || '').trim()
            : parameterValue;

        if (!rawInput || (Array.isArray(rawInput) && rawInput.length === 0)) {
            showMessage({ message: t('common:pleaseSelectParameter'), type: 'warning' });
            return;
        }

        if (isSqlNull && !validateExpr(rawInput)) {
            showMessage({ message: t('common:syntaxError'), type: 'danger' });
            return;
        }

        let chosenValues = [];
        if (isSqlNull) {
            chosenValues = rawInput.split(',').map(s => s.trim()).filter(Boolean);
        } else if (Array.isArray(parameterValue)) {
            chosenValues = parameterValue.map(v => String(v));
        } else {
            chosenValues = [String(parameterValue)];
        }

        if (chosenValues.length === 0) {
            showMessage({ message: t('common:pleaseSelectParameter'), type: 'warning' });
            return;
        }

        const isDuplicate = allCriteriaPairs.some(p => {
            if (p.criteriaId !== selectedCategoryCriteriaId) {
                return false;
            }
            const existingVal = Array.isArray(p.value) ? p.value.join('|') : String(p.value);
            const newVal = chosenValues.length > 1 ? chosenValues.join('|') : chosenValues[0];
            return existingVal === newVal;
        });

        if (isDuplicate) {
            showMessage({ message: t('common:parameterAlreadyAdded'), type: 'info' });
            return;
        }

        let parameterLabel = '';
        if (isSqlNull) {
            parameterLabel = chosenValues.length > 1 ? chosenValues.join(', ') : chosenValues[0];
        } else {
            const valueToLabel = (val) => {
                const opt = (parameterOptions || []).find(o => String(o.value) === String(val));
                return opt ? opt.label : String(val);
            };
            parameterLabel = chosenValues.length > 1 ? chosenValues.map(valueToLabel).join(', ') : valueToLabel(chosenValues[0]);
        }

        if (!parameterLabel || parameterLabel.trim() === '') {
            showMessage({ message: t('common:pleaseSelectParameter'), type: 'warning' });
            return;
        }

        const existingPair = allCriteriaPairs.find(p =>
            p.criteriaId === selectedCategoryCriteriaId &&
            JSON.stringify(p.value) === JSON.stringify(chosenValues.length > 1 ? chosenValues : chosenValues[0])
        );

        const newRowGuid = existingPair ? existingPair.rowGuid : uuidv4();
        const newPair = createCriteriaPair(selectedCriteriaObj, chosenValues, criteriaOptionsMap);
        newPair.rowGuid = newRowGuid;

        setAllCriteriaPairs(prev => {
            const index = prev.findIndex(p => p.rowGuid === newRowGuid);
            if (index !== -1) {
                const newArr = [...prev];
                newArr[index] = newPair;
                return newArr;
            }
            return [...prev, newPair];
        });

        setParameterValue('');
        if (isSqlNull) {
            setCriteriaValues(prev => ({ ...prev, [selectedCategoryCriteriaId]: '' }));
        }
    };

    const buildCriteriaListForSave = () => {
        return allCriteriaPairs.map(pair => ({
            CriteriaId: pair.criteriaId,
            Company: company,
            WorkflowId: selectedWorkflowId,
            UserId: userId,
            Value: Array.isArray(pair.value) ? pair.value.join(',') : pair.value,
            RowGuid: pair.rowGuid,
            Doc: new Date().toISOString(),
        }));
    };

    const hasChanges = () => {
        if (allCriteriaPairs.length !== initialCriteriaPairs.length) {
            return true;
        }
        // Create maps keyed by rowGuid 
        const initialMap = new Map(initialCriteriaPairs.map(p => [p.rowGuid, p]));
        for (const current of allCriteriaPairs) {
            const initial = initialMap.get(current.rowGuid);
            if (!initial) {
                return true;
            }
            if (
                current.criteriaId !== initial.criteriaId ||
                !areValuesEqual(current.value, initial.value)
            ) {
                return true;
            }
        }
        return false;
    };

    const handleSaveCriteria = async () => {
        if (!company || !selectedWorkflowId) {
            showMessage({ message: t('common:noChangesMade'), type: 'warning' });
            return;
        }
        if (!hasChanges()) {
            showMessage({ message: t('common:noChangesMade'), type: 'info' });
            return;
        }
        const invalidPairs = allCriteriaPairs.filter(pair =>
            !criteriaList.some(c => c.rowGuid === pair.criteriaId)
        );
        if (invalidPairs.length > 0) {
            showMessage({ message: t('common:invalidCriteriaId'), type: 'danger' });
            return;
        }
        // Always build criteria list to save, even if empty
        const criteriaListToSave = buildCriteriaListForSave();
        try {
            // Pass current criteriaSql and criteriaDesc 
            const success = await saveWorkflowCriteria(company, selectedWorkflowId, criteriaSql, criteriaDesc, criteriaListToSave, removedCriteriaIds);
            if (success) {
                showMessage({ message: t('common:Criteriasavedsuccessfully'), type: 'success' });
                setRemovedCriteriaIds([]);
                if (onBack) onBack();
                else navigation.goBack();
            } else {
                showMessage({ message: t('common:Failedtosavecriteria'), type: 'danger' });
            }
        } catch (error) {
            showMessage({ message: t('common:errorsavingcriteria'), type: 'danger' });
        }
    };

    const handleCancelChanges = () => {
        setRemovedCriteriaIds([]);
        setAllCriteriaPairs(initialCriteriaPairs);
        setCriteriaDesc(initialCriteriaDesc);
        setCriteriaSql('');
        setParameterValue('');
        setCriteriaValues({});
    };

    const handleGoBack = () => {
        if (onBack) {
            onBack();
        } else {
            navigation.goBack();
        }
    };

    return (
        <SafeAreaView style={styles.mainView}>
            <View style={styles.container}>
                <View style={styles.headerRow}>
                    <CustomLabel
                        value={description}
                        type="TITLE"
                        style={styles.workflowNameText}
                    />
                    <TouchableOpacity onPress={handleGoBack} style={styles.backButton}>
                        <Ionicons name="arrow-back" size={30} color="black" />
                    </TouchableOpacity>
                </View>
                <SafeAreaView style={{ flex: 1 }}>
                    <View style={[styles.buttons, styles.functionalityButtons]}>
                        <CustomButton
                            text={t('common:save')}
                            onPress={handleSaveCriteria}
                            type='BUTTON_SAVE'
                            isGradient={true}
                            textTop="0"
                            sz={15}
                        />
                        <CustomButton
                            text={t('common:cancel')}
                            onPress={handleCancelChanges}
                            type='BUTTON_CANCEL'
                            isGradient={false}
                            textTop="0"
                            sz={15}
                        />
                    </View>
                    <SafeAreaView style={common.divider} />
                    <ScrollView scrollEnabled={maxHeight === 200}
                        contentContainerStyle={{
                            maxHeight: maxHeight || 'auto',
                            paddingBottom: 20,
                        }}>
                        <View style={styles.item}>
                            <CustomLabel
                                value={t('common:categorycriteria')}
                                type='SUBTITLE'
                            />
                            <CustomDropDown
                                options={criteriaList.map(c => ({
                                    label: c.displayMember,
                                    value: c.rowGuid,
                                }))}
                                defValue={selectedCriteria}
                                setValue={(val) => {
                                    setSelectedCriteria(val);
                                    setSelectedCategoryCriteriaId(val);
                                    const selected = criteriaList.find(c => c.rowGuid === val);
                                    setIsSqlNull(!selected?.sSql);
                                }}
                                type="FULL"
                            />
                        </View>
                        <View style={styles.item}>
                            <CustomLabel
                                value={t('common:categorycriteriaparam')}
                                type='SUBTITLE'
                            />
                            <CustomDropDown
                                type='FULL'
                                defValue={parameterValue}
                                options={parameterOptions}
                                setValue={val => {
                                    setParameterValue(val);
                                    setCriteriaValues(prev => ({ ...prev, [selectedCategoryCriteriaId]: val }));
                                }}
                                disabled={isSqlNull}
                                style={isSqlNull ? styles.disabledDropdown : null}
                            />
                            <View style={[styles.buttons, styles.functionalityButtons]}>
                                <CustomButton
                                    text={t('common:add')}
                                    onPress={handleAddParameter}
                                    type='BUTTON_ADD'
                                />
                            </View>
                            {isSqlNull && (
                                <View style={{ marginHorizontal: 20, marginTop: 5 }}>
                                    <CustomInput
                                        type="MAININPUT"
                                        value={criteriaValues[selectedCategoryCriteriaId] || ''}
                                        placeholder={t('common:enterCustomValue')}
                                        setValue={(text) =>
                                            setCriteriaValues(prev => ({
                                                ...prev,
                                                [selectedCategoryCriteriaId]: text,
                                            }))
                                        }
                                        editable={true}
                                    />
                                </View>
                            )}
                        </View>
                        <View style={styles.item}>
                            <CustomLabel
                                value={t('common:worfkflowcreteria')}
                                type='SUBTITLE'
                            />
                            <View style={{ marginHorizontal: 20 }}>
                                <CustomInput
                                    type='MAININPUT'
                                    value={criteriaDesc}
                                    multiline={true}
                                    editable={false}
                                    style={{ minHeight: 80, textAlignVertical: 'top' }}
                                />
                                <ScrollView horizontal style={{ marginVertical: 10, paddingHorizontal: 20 }}>
                                    {allCriteriaPairs.filter(pair => pair.parameter && pair.parameter.trim() !== '')
                                        .map((pair, index) => (
                                            <TouchableOpacity
                                                key={index}
                                                onPress={() => {
                                                    setAllCriteriaPairs(prev => {
                                                        const removed = prev[index];
                                                        if (removed && removed.rowGuid) {
                                                            setRemovedCriteriaIds(ids => [...ids, removed.rowGuid]);
                                                        }
                                                        return prev.filter((_, i) => i !== index);
                                                    });
                                                }}
                                                style={{
                                                    paddingHorizontal: 10,
                                                    paddingVertical: 5,
                                                    marginRight: 8,
                                                    backgroundColor: '#d0d0d0',
                                                    borderRadius: 15,
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Text style={{ color: 'black' }}>
                                                    {pair.criteria} {pair.parameter}
                                                </Text>
                                            </TouchableOpacity>
                                        )
                                        )}
                                </ScrollView>
                            </View>
                        </View>
                    </ScrollView>
                </SafeAreaView>
            </View>
        </SafeAreaView>
    );
};

const makeStyles = (colors, width,) => StyleSheet.create({
    mainView: {
        flex: 1,
        marginHorizontal: 10,
    },
    container: {
        marginBottom: 10,
        flex: 1,
    },
    headerRow: {
        height: 16,
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 10,
        marginBottom: 10,
        justifyContent: 'space-between',
    },
    backButton: {
        padding: 5,
    },
    workflowNameText: {
        fontWeight: 'bold',
        fontSize: 20,
        color: colors.text,
        flex: 1,
    },
    functionalityButtons: {
        justifyContent: 'flex-end',
    },
    buttons: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        width: '50%',
        alignSelf: 'flex-end',
        marginRight: 15,
    },
    disabledDropdown: {
        backgroundColor: '#afacac',
        opacity: 0.8,
    },
});

export default HRAdminWorkflowCriteria;